import { matchSorter, rankings } from 'match-sorter';
import type { KeyOption, MatchSorterOptions } from 'match-sorter';

import { approximatelyHolds } from './approximate-match.util';
import type { SearchTolerance } from './search-tolerance.util';

// A key whose texts are also searched with the typos of a tolerance.
export interface TolerantKey<I> {
  readonly key: (item: I) => string | string[];
  readonly tolerance: SearchTolerance;
}

// The items matching every word of the search, each one in at least one of the keys: their texts
// contain the word, whatever its case and accents (the default threshold, MATCHES, keeps any
// text holding its letters in order, far apart). Then, the items whose tolerant keys hold the
// word with the typos of their tolerance.
export const filteredItemsOf = <I>({
  items,
  search,
  keys,
  tolerantKeys = [],
  options,
}: Readonly<{
  items: readonly I[];
  search: string;
  keys: readonly KeyOption<I>[];
  tolerantKeys?: readonly TolerantKey<I>[];
  options?: Omit<MatchSorterOptions<I>, 'keys'>;
}>): readonly I[] =>
  search
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .reduce<readonly I[]>((candidates, word) => {
      const exactMatches = matchSorter(candidates, word, {
        threshold: rankings.CONTAINS,
        ...options,
        keys: [...keys],
      });
      const tolerantMatches = candidates.filter(
        (item) =>
          !exactMatches.includes(item) &&
          tolerantKeys.some(({ key, tolerance }) =>
            [key(item)]
              .flat()
              .some((text) => approximatelyHolds(text, word, tolerance)),
          ),
      );

      return [...exactMatches, ...tolerantMatches];
    }, items);
